/**
 * Die neun Farben eines **Canvas-Elements** — literale Hex und keine Tokens
 * (Gestaltung von Nutzerinhalt, nicht von Bedienoberfläche): Die
 * `--colors/*`-Leiter ist laut CLAUDE.md für neue Komponenten gesperrt und
 * flippt im Dark Mode, ein Zettel behält aber seine Farbe.
 *
 * Die Wählertöne (`swatch`) und die Leisten-Rollen (`bar`) sind aus dem Figma
 * gemessen (Datei `8T1uMmZugRNP6eTh1rqJPK`, Node `5083:51492`).
 *
 * **Die Flächen (`hex`) sind es NICHT mehr.** Die Figma-Flächen (`5083:52029`)
 * waren eine Spur heller und blasser als die Töne davor, und der Unterschied
 * war auf dem Canvas zu sehen — die Zettel wirkten ausgewaschen. Deshalb
 * stehen hier wieder die Töne von vor dem 1. September 2026: die
 * Tailwind-100-Stufe der jeweiligen Familie (Gelb ein eigener, etwas
 * satterer Ton). Die drei neuen Farben, die es damals noch nicht gab, sind
 * in derselben Logik ergänzt: Rot red-100, Grau ein zinc-getöntes Hellgrau
 * zwischen 100 und 200 (zinc-100 stünde neben Weiß fast unsichtbar), Weiß
 * bleibt Weiß.
 *
 * **Drei Töne je Farbe, und das ist Absicht:**
 * - `hex` — die Fläche des **Notiz**. Die der **Memo** steht in
 *   `canvasMemoStyle.ts`, sie ist heller, weil dort dunkler Text darauf
 *   steht.
 * - `swatch` — derselbe Farbwert im **Farbwähler**, aber kräftiger. Auf
 *   16px Tropfengröße wären die Flächentöne nicht zu unterscheiden; man wählt
 *   also ein deutlicheres Abbild dessen, was man bekommt.
 * - `bar` — die vier Rollen der **Options-Leiste**, die den Ton ihres Elements
 *   trägt. Es sind die Tailwind-Stufen 950/900/50/200 der Familie; die
 *   Zuordnung Farbe → Familie steht in `family` und ist für Weiß und Grau
 *   festgelegt worden, weil es dafür keine Familie gibt.
 *
 * **Die Reihenfolge ist die des Farbwählers**, nicht die der Häufigkeit —
 * Weiß steht vorn. Die Vorgabe ist trotzdem Gelb (`DEFAULT_CANVAS_NOTE_COLOR`)
 * und wird deshalb ausdrücklich benannt statt über den ersten Eintrag geholt.
 *
 * Der weiße Verlauf, der über der Fläche liegt, steht als Rezept in
 * `NoteNode.scss` — er ist für alle Farben derselbe.
 */
/*
  **Jede Farbe kommt in ZWEI Stufen**, hell und dunkel — wie in FigJam. Die
  dunkle Stufe steht direkt hinter ihrer hellen, trägt deren Schlüssel mit
  `-dark` und ist am Zettel gespeichert wie jede andere Farbe: als Schlüssel,
  ohne zweites Feld. Ältere Fassungen, die den Schlüssel nicht kennen, fallen
  auf Gelb zurück.

  Die dunkle Stufe ist ein MITTELTON, kein Nachtblau: Tailwind-Stufe 300 der
  Familie als Fläche, 400 im Wähler. Der Text bleibt dunkel, und die Leiste
  färbt sich wie bei der hellen Schwester — sie folgt der Familie, nicht der
  Stufe. Gelb-300 (#FDE047) ist auf einem Zettel grell und bekommt einen
  eigenen, etwas ruhigeren Ton.
*/
export interface CanvasNoteBarColors {
  /** Fläche der Leiste — Stufe 950. */
  surface: string
  /** Die 1px-Trenner zwischen den Knöpfen — Stufe 900. Auch der Hover-Grund. */
  divider: string
  /** Icons, Schriftname und die Füllung des Farb-Tropfens — Stufe 50. */
  fg: string
  /** Der Rand des Farb-Tropfens — Stufe 200. */
  chipBorder: string
}

/*
  `fold`/`foldHover` — der Knick des **Eselsohrs**, nur bei den TONLOSEN
  Farben.

  Sonst wird er aus der Fläche gerechnet (`oklch(from … calc(l - 0.08)
  calc(c + 0.03) h)` in `NoteNode.scss`): eine Spur dunkler und bunter,
  damit jede Farbe gleich weit vom Papier abrückt. Weiß und Grau haben aber
  keinen Farbton, an dem sich die Buntheit festmachen könnte — der Zuschlag
  landete bei Hue 0, und der Knick wurde rosa.

  Dieselbe Vorkehrung wie bei der **Notiz** (`canvasMemoStyle.ts`), und
  aus demselben Grund. Der Hover-Ton muss mit: Der Wert steht am Zettel als
  Inline-Variable, und die schlägt jede Regel aus der SCSS.
*/
export const CANVAS_NOTE_COLORS = [
  { key: 'white', hue: 'white', shade: 'light', family: 'neutral', hex: '#FFFFFF', swatch: '#FFFFFF', fold: '#ebebeb', foldHover: '#dcdcdc', bar: { surface: '#0a0a0a', divider: '#171717', fg: '#fafafa', chipBorder: '#e5e5e5' } },
  { key: 'white-dark', hue: 'white', shade: 'dark', family: 'neutral', hex: '#E5E5E5', swatch: '#D4D4D4', fold: '#d4d4d4', foldHover: '#c4c4c4', bar: { surface: '#0a0a0a', divider: '#171717', fg: '#fafafa', chipBorder: '#e5e5e5' } },
  { key: 'gray', hue: 'gray', shade: 'light', family: 'zinc', hex: '#ECECEE', swatch: '#D9D9D9', fold: '#dadada', foldHover: '#cbcbcb', bar: { surface: '#09090b', divider: '#18181b', fg: '#fafafa', chipBorder: '#e4e4e7' } },
  { key: 'gray-dark', hue: 'gray', shade: 'dark', family: 'zinc', hex: '#D4D4D8', swatch: '#A1A1AA', fold: '#c2c2c6', foldHover: '#b1b1b6', bar: { surface: '#09090b', divider: '#18181b', fg: '#fafafa', chipBorder: '#e4e4e7' } },
  // **Beige** steht bei den Neutralen und nicht zwischen Orange und Gelb: Es
  // ist der warme Grundton, das Gegenstück zu Weiß und Grau — Papier, nicht
  // Farbe. Familie `stone`, Tailwinds warmes Grau; die Fläche liegt
  // allerdings deutlich wärmer als `stone-100`, das kaum von Grau abwiche.
  { key: 'beige', hue: 'beige', shade: 'light', family: 'stone', hex: '#F7F1E4', swatch: '#E3D2B0', bar: { surface: '#22190f', divider: '#3d3123', fg: '#faf7f2', chipBorder: '#e0d3bb' } },
  // Beige-dark: warmer Sand statt stone-300 — das war auf dem Zettel ein
  // schlammiges Grau, das niemand wählt. Kraftpapier, eine Spur goldener.
  { key: 'beige-dark', hue: 'beige', shade: 'dark', family: 'stone', hex: '#E9D6B4', swatch: '#D1B383', bar: { surface: '#22190f', divider: '#3d3123', fg: '#faf7f2', chipBorder: '#e0d3bb' } },
  { key: 'red', hue: 'red', shade: 'light', family: 'red', hex: '#FEE2E2', swatch: '#FFB8B9', bar: { surface: '#450a0a', divider: '#7f1d1d', fg: '#fef2f2', chipBorder: '#fecaca' } },
  { key: 'red-dark', hue: 'red', shade: 'dark', family: 'red', hex: '#FCA5A5', swatch: '#F87171', bar: { surface: '#450a0a', divider: '#7f1d1d', fg: '#fef2f2', chipBorder: '#fecaca' } },
  { key: 'orange', hue: 'orange', shade: 'light', family: 'orange', hex: '#FFEDD5', swatch: '#FFE8CC', bar: { surface: '#431407', divider: '#7c2d12', fg: '#fff7ed', chipBorder: '#fed7aa' } },
  { key: 'orange-dark', hue: 'orange', shade: 'dark', family: 'orange', hex: '#FDBA74', swatch: '#FB923C', bar: { surface: '#431407', divider: '#7c2d12', fg: '#fff7ed', chipBorder: '#fed7aa' } },
  { key: 'green', hue: 'green', shade: 'light', family: 'emerald', hex: '#D1FAE5', swatch: '#CCFFE5', bar: { surface: '#022c22', divider: '#064e3b', fg: '#ecfdf5', chipBorder: '#a7f3d0' } },
  { key: 'green-dark', hue: 'green', shade: 'dark', family: 'emerald', hex: '#6EE7B7', swatch: '#34D399', bar: { surface: '#022c22', divider: '#064e3b', fg: '#ecfdf5', chipBorder: '#a7f3d0' } },
  // Gelb bekommt keinen Ton aus der eigenen Familie: `yellow-950` (#422006)
  // ist auf dem Zettel schlicht Braun, ein Neutral dagegen kalter Kaffee.
  // Amber-900 (#78350f) war wiederum zu hell. Dazwischen, per Sichtprüfung:
  // ein tiefes Orange-Braun, dunkler als Amber-900, wärmer als Yellow-950.
  // Der Trenner entsättigt (gleiche Helligkeit wie #8a4513, Chroma halbiert),
  // damit er nicht als eigene Farbe auffällt.
  { key: 'yellow', hue: 'yellow', shade: 'light', family: 'yellow', hex: '#FFF7A3', swatch: '#FFFBCC', bar: { surface: '#4d2408', divider: '#6e4a33', fg: '#fffbeb', chipBorder: '#fde68a' } },
  { key: 'yellow-dark', hue: 'yellow', shade: 'dark', family: 'yellow', hex: '#FCE96A', swatch: '#FACC15', bar: { surface: '#4d2408', divider: '#6e4a33', fg: '#fffbeb', chipBorder: '#fde68a' } },
  { key: 'blue', hue: 'blue', shade: 'light', family: 'cyan', hex: '#E0F2FE', swatch: '#CCEAFF', bar: { surface: '#083344', divider: '#164e63', fg: '#ecfeff', chipBorder: '#a5f3fc' } },
  // Blue-dark: ein Himmelblau statt cyan-300 — das schlug ins Türkis.
  { key: 'blue-dark', hue: 'blue', shade: 'dark', family: 'cyan', hex: '#71CEF9', swatch: '#4CBDF3', bar: { surface: '#083344', divider: '#164e63', fg: '#ecfeff', chipBorder: '#a5f3fc' } },
  // **Indigo** füllt die größte Lücke im Farbkreis: Zwischen dem blassen Blau
  // (das eigentlich ein Cyan ist) und Violett lagen 65°, und wer „blau"
  // meint, greift meistens hierher.
  { key: 'indigo', hue: 'indigo', shade: 'light', family: 'indigo', hex: '#E0E7FF', swatch: '#C2CEFF', bar: { surface: '#1e1b4b', divider: '#312e81', fg: '#eef2ff', chipBorder: '#c7d2fe' } },
  { key: 'indigo-dark', hue: 'indigo', shade: 'dark', family: 'indigo', hex: '#A5B4FC', swatch: '#818CF8', bar: { surface: '#1e1b4b', divider: '#312e81', fg: '#eef2ff', chipBorder: '#c7d2fe' } },
  { key: 'purple', hue: 'purple', shade: 'light', family: 'violet', hex: '#EDE9FE', swatch: '#D9CCFF', bar: { surface: '#2e1065', divider: '#4c1d95', fg: '#f5f3ff', chipBorder: '#ddd6fe' } },
  { key: 'purple-dark', hue: 'purple', shade: 'dark', family: 'violet', hex: '#C4B5FD', swatch: '#A78BFA', bar: { surface: '#2e1065', divider: '#4c1d95', fg: '#f5f3ff', chipBorder: '#ddd6fe' } },
  { key: 'pink', hue: 'pink', shade: 'light', family: 'pink', hex: '#FCE7F3', swatch: '#FFCCEB', bar: { surface: '#500724', divider: '#831843', fg: '#fdf2f8', chipBorder: '#fbcfe8' } },
  { key: 'pink-dark', hue: 'pink', shade: 'dark', family: 'pink', hex: '#F9A8D4', swatch: '#F472B6', bar: { surface: '#500724', divider: '#831843', fg: '#fdf2f8', chipBorder: '#fbcfe8' } },
] as const

export type CanvasNoteColorKey = (typeof CANVAS_NOTE_COLORS)[number]['key']
export type CanvasNoteHueKey = (typeof CANVAS_NOTE_COLORS)[number]['hue']
export type CanvasNoteShade = 'light' | 'dark'
export type CanvasNoteColorEntry = (typeof CANVAS_NOTE_COLORS)[number]

/**
 * Die elf **Farben** — je eine helle Stufe, in der Reihenfolge des
 * Farbwählers. Die dunkle Zwillingsstufe jeder Farbe holt man über
 * `getCanvasNoteShade(hue, 'dark')`.
 */
export const CANVAS_NOTE_HUES = CANVAS_NOTE_COLORS.filter(c => c.shade === 'light')

/** Eine Farbe in einer ihrer zwei Stufen. Unbekannte Farbe fällt auf Gelb zurück. */
export function getCanvasNoteShade(hue: string, shade: CanvasNoteShade): CanvasNoteColorEntry {
  return CANVAS_NOTE_COLORS.find(c => c.hue === hue && c.shade === shade)
    ?? CANVAS_NOTE_COLORS.find(c => c.hue === DEFAULT_CANVAS_NOTE_COLOR && c.shade === shade)!
}

/**
 * Gelb, wie vor der Erweiterung auf neun Farben — und ausdrücklich benannt
 * statt über `CANVAS_NOTE_COLORS[0]` geholt: Vorn steht jetzt Weiß, und ein
 * Zettel, der weiß auf fast weißem Grund zur Welt käme, wäre kaum zu sehen.
 */
export const DEFAULT_CANVAS_NOTE_COLOR: CanvasNoteColorKey = 'yellow'

const DEFAULT_CANVAS_NOTE_COLOR_ENTRY = CANVAS_NOTE_COLORS.find(c => c.key === DEFAULT_CANVAS_NOTE_COLOR)!

/** Der ganze Eintrag. Unbekannte Schlüssel fallen auf Gelb zurück. */
export function getCanvasNoteColor(key: string | null) {
  return CANVAS_NOTE_COLORS.find(c => c.key === key) ?? DEFAULT_CANVAS_NOTE_COLOR_ENTRY
}

/**
 * Wie eine Farbwahl auf eine MEHRFACHAUSWAHL wirkt.
 *
 * Stehen in der Auswahl beide Stufen derselben oder verschiedener Farben,
 * wechselt nur die FARBE — jeder Zettel behält seine Stufe. Wer ein helles
 * und ein dunkles Grün gestuft hat und die Gruppe rot färbt, will ein
 * helles und ein dunkles Rot, nicht zwei gleiche. Ist die Stufe in der
 * Auswahl einheitlich, gilt die gewählte Farbe wörtlich, Stufe inklusive.
 *
 * Liefert die Abbildung Zettelfarbe → neue Farbe für genau diese Auswahl.
 */
export function bulkColorFor(picked: string, colors: readonly (string | null)[]): (current: string | null) => string {
  const shades = new Set(colors.map(c => getCanvasNoteColor(c).shade))
  if (shades.size < 2) return () => picked
  const hue = getCanvasNoteColor(picked).hue
  return current => getCanvasNoteShade(hue, getCanvasNoteColor(current).shade).key
}

/** Fläche einer Notiz. Unbekannte Schlüssel fallen auf Gelb zurück. */
export function getCanvasNoteColorHex(key: string | null): string {
  return getCanvasNoteColor(key).hex
}

/**
 * Die vier Rollenfarben der **Options-Leiste** für ein Element dieser Farbe.
 *
 * `null` liefert nicht die Vorgabefarbe, sondern **Neutral**: Für eine
 * Mehrfachauswahl gibt es keine gemeinsame Farbe, und die Leiste am
 * **Auswahlrahmen** ist deshalb neutral (siehe CONTEXT.md, „Options-Leiste").
 */
export function getCanvasNoteBarColors(key: string | null | undefined): CanvasNoteBarColors {
  if (key == null) return CANVAS_NOTE_COLORS[0].bar
  return getCanvasNoteColor(key).bar
}

/*
  Der Ton des **Tauschpunkts** für die drei TONLOSEN Farben.

  Sonst trägt der Ring den `swatch` der dunklen Stufe — den kräftigsten Ton
  der Familie. Bei Weiß wäre das `#D4D4D4`: mit seinem weißen Rand auf weißem
  Papier verschwände er. Die Text-Notiz hat für dieselben Farben schon eine
  Bleistiftlinie (`border` der dunklen Stufe in `canvasMemoStyle.ts`),
  und genau die ist es hier.
*/
const NEUTRAL_RING_COLORS: Partial<Record<CanvasNoteHueKey, string>> = {
  white: '#a3a3a3',
  gray: '#a1a1aa',
  beige: '#c4a574',
}

/**
 * Die Farbe des **Tauschpunkts** auf einem Zettel dieser Farbe.
 *
 * EIN Akzent je Farbe, unabhängig von der Stufe: der `swatch` der dunklen
 * Stufe. Der helle swatch ist blasser als das Papier selbst (gelb `#FFFBCC`
 * auf `#FFF7A3`), die Tinte der Leiste (`bar.surface`) ein Loch im Papier.
 * Unbekannte Farbe fällt auf Gelb zurück, wie überall hier.
 */
export function getCanvasNoteRingColor(key: string | null | undefined): string {
  const { hue } = getCanvasNoteColor(key ?? null)
  return NEUTRAL_RING_COLORS[hue] ?? getCanvasNoteShade(hue, 'dark').swatch
}

/**
 * Der feste Emoji-Satz eines **Notiz**.
 *
 * Er wird derzeit nur noch ANGEZEIGT: Der Emoji-Knopf der **Options-Leiste**
 * ist ein Platzhalter, bis sein Wähler entworfen ist (siehe
 * `.claude/tasks/canvas-options-bar-redesign.md`). Bestehende Emojis bleiben
 * dabei stehen. `null` heißt „keins".
 */
export const CANVAS_NOTE_EMOJIS: (string | null)[] = [null, '💡', '⭐', '🔥', '✅', '❤️', '🎯']
